/**
 * RoleToggle — Spec 112: Switch Role Support
 *
 * A compact toggle button shown in the navbar for users with dynamic_role = 'switch'.
 * Displays the current role label (Dom/Sub) and allows one-click toggling between roles.
 *
 * Only renders when the user is a designated switch in the current hierarchy.
 */

import React, { useState } from 'react';
import { useTerminology } from '../../hooks/useTerminology';

interface RoleToggleProps {
  isSwitch: boolean;
  effectiveRole: 'dominant' | 'submissive';
  onToggle: () => Promise<void>;
}

const RoleToggle: React.FC<RoleToggleProps> = ({ isSwitch, effectiveRole, onToggle }) => {
  const { t } = useTerminology();
  const [toggling, setToggling] = useState(false);

  if (!isSwitch) {
    return null;
  }

  const isDominant = effectiveRole === 'dominant';

  const handleToggle = async () => {
    if (toggling) return;
    setToggling(true);
    // bug-registry 0zzz-cx: the failure path for this toggle is handled at SOURCE, in
    // useActiveRole.ts's toggleRole (~:141-153) — it try/catches, calls showError() and never
    // rethrows, so onToggle() cannot reject. Verified at that file, not assumed. A catch here
    // would be unreachable code handling an impossible rejection (CLAUDE.md Simplicity First).
    // missing-catch exempt: onToggle -> useActiveRole.toggleRole catches internally and never rejects
    try {
      await onToggle();
    } finally {
      setToggling(false);
    }
  };

  return (
    <button
      className={`inline-flex items-center gap-1 px-2.5 py-1 border rounded-xl text-[0.8rem] font-medium cursor-pointer whitespace-nowrap transition-all duration-200 bg-surface
        ${isDominant ? 'border-accent hover:border-accent/80' : 'border-border-medium hover:border-border-medium/80'}
        disabled:opacity-60 disabled:cursor-not-allowed
        md:px-2 md:py-[3px] md:gap-[3px]`}
      onClick={handleToggle}
      disabled={toggling}
      title={`Currently ${isDominant ? t('dominant') : t('submissive')}. Click to switch.`}
      aria-label={`Switch from ${effectiveRole} role`}
    >
      <span className="text-xs uppercase tracking-wide">
        {toggling ? 'Switching...' : isDominant ? 'Dom' : 'Sub'}
      </span>
    </button>
  );
};

export default RoleToggle;
